import { useMemo, useState, type FC } from "react";
import ChartLegend from "@/components/ChartLegend";
import PillBarList from "@/components/PillBarList";
import ChartSwitcher from "@/components/ChartSwitcher";
import ChartStateBody from "@/pages/CampaignOverviewPage/components/ChartStateBody";
import {
  buildCreativeBarRows,
  buildSizeSlices,
  buildImpressionsBySizeOption,
} from "@/pages/CampaignOverviewPage/charts/creativeChartHelpers";
import { formatShare } from "@/pages/CampaignOverviewPage/charts/chartHelpers";
import { formatCompactImpressions } from "@/pages/CampaignOverviewPage/charts/pillBarChart";
import useDarkMode from "@/hooks/useDarkMode";
import type { PerformanceReportRow } from "@/types/reporting";
import { TEXT } from "@/constants/text";
import styles from "@/pages/CampaignOverviewPage/widgets/CreativePerformanceWidget.module.css";

const CREATIVE_TEXT = TEXT.campaignOverview.creativePerformance;

const TOP_N_OPTIONS = [
  { key: "top", label: CREATIVE_TEXT.showTop10 },
  { key: "all", label: CREATIVE_TEXT.showAll },
] as const;

// Donut renders at a fixed 220px; the legend sits below it.
const SIZE_DONUT_HEIGHT = "13.75rem";

interface CreativePerformanceWidgetProps {
  error: string | null;
  isLoading: boolean;
  rows: PerformanceReportRow[];
}

const CreativePerformanceWidget: FC<CreativePerformanceWidgetProps> = ({
  error,
  isLoading,
  rows,
}) => {
  const isDark = useDarkMode();
  const [showAll, setShowAll] = useState(false);

  const creativeBarRows = useMemo(
    () => buildCreativeBarRows(rows, showAll),
    [rows, showAll]
  );

  const sizeLegendItems = buildSizeSlices(rows, isDark).map(
    ({ color, name, share }) => ({
      color,
      label: name,
      value: formatShare(share),
    })
  );

  const impressionsBySizeOption = useMemo(
    () => buildImpressionsBySizeOption(rows, isDark),
    [rows, isDark]
  );

  return (
    <>
      {/* Chart 1 — Impressions by Creative (horizontal bar, top-N toggle) */}
      <div className={styles.card}>
        <div className={styles.header}>
          <span className={styles.title}>
            {CREATIVE_TEXT.impressionsByCreativeTitle}
          </span>
          {!isLoading && !error && rows.length > 0 && (
            <ChartSwitcher
              label={TEXT.campaignOverview.switcher.rowsShown}
              options={TOP_N_OPTIONS}
              value={showAll ? "all" : "top"}
              onChange={(key) => setShowAll(key === "all")}
            />
          )}
        </div>
        {isLoading && <div className={styles.chartSkeleton} />}
        {!isLoading && error && (
          <p className={styles.message}>{CREATIVE_TEXT.error}</p>
        )}
        {!isLoading && !error && rows.length === 0 && (
          <p className={styles.message}>{CREATIVE_TEXT.emptyMessage}</p>
        )}
        {!isLoading && !error && rows.length > 0 && (
          <div className={styles.chartScroll} data-pdf-clip="true">
            <PillBarList
              formatValue={formatCompactImpressions}
              rows={creativeBarRows}
            />
          </div>
        )}
      </div>

      {/* Impressions by Creative Size (flat donut + legend) */}
      <div className={styles.card}>
        <div className={styles.header}>
          <span className={styles.title}>
            {CREATIVE_TEXT.impressionsBySizeTitle}
          </span>
        </div>
        <ChartStateBody
          error={error}
          errorMessage={CREATIVE_TEXT.error}
          isEmpty={rows.length === 0}
          isLoading={isLoading}
          loadingClassName={styles.chartSkeletonSmall}
          messageClassName={styles.message}
          noDataMessage={CREATIVE_TEXT.emptyMessage}
          option={impressionsBySizeOption}
          style={{ height: SIZE_DONUT_HEIGHT, width: "100%" }}
        />
        {!isLoading && !error && rows.length > 0 && (
          <ChartLegend items={sizeLegendItems} twoColumns />
        )}
        <p className={styles.sizeNote}>{CREATIVE_TEXT.sizeNote}</p>
      </div>
    </>
  );
};

export default CreativePerformanceWidget;
